import { getFunctions, httpsCallable } from 'firebase/functions';
import * as _ from 'lodash';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useFirestore } from 'reactfire';
import { childrenProps } from 'src/commonTypes/childrenProps';
import { useAlertContext } from 'src/context/AlertContext';
import { AuthContext } from 'src/context/AuthContext';
import {
  fruitProfileFisherman,
  fruitProfileRemove,
  fruitProfileWrite,
  textedCounter,
} from 'src/views/harvesting/fruit-profile/fruit-profile-single/fruit-profile-write';
import {
  mergeFruitProfiles,
  mergedFruit,
  oldFruitsProfileToNewFormatReader,
} from 'src/views/harvesting/fruit-profile/fruit-profile-single/utils/document-version-reader';
import {
  isFruitTextedToday,
  nameHasSpeicalCharacters,
  smartFilteringStatus,
  smartListMerging,
} from 'src/views/harvesting/fruit-profile/fruit-profile-single/utils/smart-filtering';
import { fishesPreloader } from './fruit-profile/fruit-profile-single/fruit-preloader';
import {
  FishermansType,
  FruitPropsType,
  FruitStatusType,
  centerClassType,
  checkedFishType,
} from './fruit-profile/model';
// export {FruitPropsType};

interface HarvestingContextType {
  allFruits: FruitPropsType[];
  getFruitsBystatus: (status: FruitStatusType) => FruitPropsType[];
  setFruitStatus: (
    targetFruit: FruitPropsType,
    status: FruitStatusType
  ) => void;
  addFruit: (fruit: FruitPropsType) => void;
  addFruits: (fruits: FruitPropsType[]) => void;
  removeFruit: (fruit: FruitPropsType, checkDataOld: any) => void;
  availableCount: number;
  textedCount: number;
  filteredCount: number;
  blacklistedCount: number;
  allCount: number;
  removedCount: number;
  resetBaskets: () => void;
  setFruitTexted: (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => void;
  setFruitNewBlacklisted: (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false,
    blacklistReason: string,
    blacklistCenterClass: centerClassType
  ) => void;
  setFruitAsMember: (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => void;
  setFruitAsPastor: (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => void;
  reorderBasket: (startIndex: number, endIndex: number) => void;
  getAllFruitsGroup: () => string[];
  requestedFacebookGroups: string[];
  addRequestGroupId: (gid: string) => void;
  setFruitNewLocked: (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => void;
  setFruitAsUnTexted: (checkedFruit: FruitPropsType | false) => void;
  removeRequestedGroupId: (gid: string) => void;
  removeFruitLocks: (fruit: FruitPropsType, checkData: any[]) => void;
  checkedFruit: checkedFishType;
}

const HarvestingContext = createContext({} as HarvestingContextType);

export function useHarvestingContext() {
  return useContext(HarvestingContext);
}

export const HarvestingProvider: React.FC<childrenProps> = ({ children }) => {
  const initialState = JSON.parse(
    localStorage.getItem('fruits') || '[]'
  ) as FruitPropsType[];
  const [fruits, setFruits] = useState<FruitPropsType[]>(initialState);
  const [checkedFruit, setCheckedFruit] = useState<checkedFishType>({});
  const { user } = useContext(AuthContext);

  // useEffect(() => {
  //   fishesPreloader(db,fruits.map(fruit=>fruit.name),checkedFruit,setCheckedFruit);
  // }, []);
  useEffect(() => {
    fishesPreloader(
      db,
      fruits.map((fruit) => fruit.name),
      checkedFruit,
      setCheckedFruit
    );
    const igNeedfetchFruits = fruits.filter(
      (fruit) =>
        fruit.image &&
        fruit.platform === 'Instagram' &&
        !fruit.image?.includes('combine-harvester-imgproxy')
    );
    if (igNeedfetchFruits.length > 0) {
      fetchInstagramPics(fruits, igNeedfetchFruits);
    } else {
      localStorage.setItem('fruits', JSON.stringify(fruits));
    }
  }, [fruits]);

  const allFruits = fruits;
  const { useErrorAlert, useAlert } = useAlertContext();
  const db = useFirestore();
  const getFruitsBystatus = (status: FruitStatusType) => {
    if (status === 'All') {
      return allFruits;
    }
    return fruits.filter((fruit: FruitPropsType) => {
      if (fruit.status === 'Removed') {
        return status === 'Removed';
      }
      const thisFruitName = fruit.name.toLowerCase().trim();

      const thisCheckedFruit = checkedFruit[thisFruitName] ?? [];
      const fruitStatus =
        checkedFruit[thisFruitName] === undefined ? 'loading' : 'success';
      const DataWithNewVersion =
        fruitStatus === 'success'
          ? oldFruitsProfileToNewFormatReader(thisCheckedFruit)
          : false;
      const thisFruitStatus: FruitStatusType =
        DataWithNewVersion === false
          ? 'Available'
          : smartFilteringStatus(fruit, DataWithNewVersion, user.name);
      return thisFruitStatus === status;
    });
  };

  const functions = getFunctions();
  const instagramPics = httpsCallable(functions, 'instagramPics');

  const setFruitStatus = (
    targetFruit: FruitPropsType,
    status: FruitStatusType
  ) => {
    const updatedFruits = fruits.map((fruit) => {
      if (fruit.id === targetFruit.id) {
        return { ...fruit, status };
      }
      return fruit;
    });
    setFruits(updatedFruits);
  };
  const getCurrentTime = () => {
    return { seconds: (Date.now() / 1000) | 0, nanoseconds: 0 };
  };

  const setFruitTexted = async (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => {
    if (checkedFruit && isFruitTextedToday(checkedFruit)) {
      useErrorAlert(
        'Some problem with fruit merging system',
        new Error('a checked fruit is a texted fruit today')
      );
    } else {
      const fishermans: FishermansType[] = [
        {
          name: user.name,
          createdAt: getCurrentTime(),
          methodType: targetFruit.methodType ?? 'Not sure',
        },
      ];

      const targetFruitWithFishman: FruitPropsType = {
        ...targetFruit,
        name: targetFruit.name.toLowerCase().trim(),
        fishermans,
        fisherman: user.name,
        createdAt: getCurrentTime(),
      };

      const mergeFruits = checkedFruit
        ? mergeFruitProfiles([targetFruitWithFishman, checkedFruit])
        : targetFruitWithFishman;

      if (mergeFruits) {
        fruitProfileWrite(db, user.name, mergeFruits, {}, true)
          .then(() => {
            textedCounter(
              db,
              mergeFruits.id,
              mergeFruits.name,
              user.id,
              user.name,
              user.group,
              user.team,
              'increase'
            ).catch((error) => {
              useErrorAlert('Can\'t calculate texted result', error);
            });
          })
          .catch((error) => {
            useErrorAlert('Can\'t report texted result', error);
          });
      } else {
        useErrorAlert(
          'Some problem with fruit merging system',
          new Error('mergeFruits has no fruit')
        );
      }
    }
  };

  const setFruitNewBlacklisted = async (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false,
    blacklistReason: string,
    blacklistCenterClass: centerClassType
  ) => {
    const mergeFruits = mergedFruit(targetFruit, checkedFruit);

    if (mergeFruits) {
      await fruitProfileWrite(
        db,
        user.name,
        mergeFruits,
        {
          isBlacklist: true,
          blacklistReason,
          blacklistCenterClass,
        },
        true
      );
    } else {
      useErrorAlert(
        'Some problem with blacklisting fruit feature',
        new Error('mergeFruits is empty when blacklist')
      );
    }
  };

  const setFruitNewLocked = async (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => {
    //TODO: NOT SURE WHETHER IT IS RIGHT
    const mergeFruits = mergedFruit(targetFruit, checkedFruit);
    await fruitProfileWrite(db, user.name, mergeFruits, {});
  };
  const removeFruitLocks = async (fruit: FruitPropsType, checkData: any[]) => {
    const fruitsNeedToBeRemoved = checkData.filter((x) => x.id !== fruit.id);
    await fruitProfileWrite(db, user.name, fruit, {}, true);

    //remove duplicated doc. e.g extra v3 doc, v2 doc
    fruitProfileRemove(db, fruitsNeedToBeRemoved);
  };

  const setFruitAsMember = async (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => {
    const mergeFruits = mergedFruit(targetFruit, checkedFruit);

    if (mergeFruits) {
      console.log('mergeFruits', mergeFruits.id);
      await fruitProfileWrite(
        db,
        user.name,
        mergeFruits,
        {
          isFamilyMember: true,
        },
        true
      );
    } else {
      useErrorAlert(
        'Some problem with member fruit feature',
        new Error('mergeFruits is empty when setFruitAsMember')
      );
    }
  };

  const setFruitAsPastor = async (
    targetFruit: FruitPropsType,
    checkedFruit: FruitPropsType | false
  ) => {
    const mergeFruits = mergedFruit(targetFruit, checkedFruit);

    if (mergeFruits) {
      await fruitProfileWrite(
        db,
        user.name,
        mergeFruits,
        {
          isPastor: true,
        },
        true
      );
    } else {
      useErrorAlert(
        'Some problem with member fruit feature',
        new Error('mergedFruit is null when setFruitaspastor')
      );
    }
  };

  const setFruitAsUnTexted = async (checkedFruit: FruitPropsType | false) => {
    if (checkedFruit) {
      const newCheckedFruit = checkedFruit;
      newCheckedFruit.fishermans = checkedFruit.fishermans?.splice(1);
      if (
        Array.isArray(newCheckedFruit.fishermans) &&
        newCheckedFruit.fishermans.length > 0
      ) {
        newCheckedFruit.fisherman = newCheckedFruit.fishermans[0].name;
        newCheckedFruit.createdAt = newCheckedFruit.fishermans[0].createdAt;
        fruitProfileWrite(db, user.name, newCheckedFruit, {}, true).then(() => {
          textedCounter(
            db,
            checkedFruit.id,
            checkedFruit.name,
            user.id,
            user.name,
            user.group,
            user.team,
            'decrease'
          ).catch((error) => {
            useErrorAlert('Can\'t calculate texted result', error);
          });
        });
      } else {
        fruitProfileFisherman(db, checkedFruit).then(() => {
          textedCounter(
            db,
            checkedFruit.id,
            checkedFruit.name,
            user.id,
            user.name,
            user.group,
            user.team,
            'decrease'
          ).catch((error) => {
            useErrorAlert('Can\'t calculate texted result', error);
          });
        });
      }
    } else {
      useErrorAlert(
        'Some problem with untext fruit feature',
        new Error('check Fruit is false when set fruit as untexted')
      );
    }
  };

  const removeFruit = (removedFruit: FruitPropsType, checkDataOld: any) => {
    // const updatedFruits = fruits.filter((fruit) => {
    //   return fruit.id !== removedFruit.id;
    // });
    // setFruits(updatedFruits);
    removeFruitLocks(removedFruit, checkDataOld);
    setFruitStatus(removedFruit, 'Removed');
  };

  //TODO: UNIT TESTING FOR THIS LOGIC, VERY IMPORTANT
  const addFruits = (newFruits: FruitPropsType[]) => {
    const fruitsWithoutSpecialCharacters = newFruits.filter(
      (x) => !nameHasSpeicalCharacters(x.name)
    );

    if (fruitsWithoutSpecialCharacters.length === 0) {
      useAlert(
        'Fruit name should not contain any special characters',
        'warning'
      );
    } else {
      const { uniqueNewFruits, newFruitsList } = smartListMerging(
        fruits,
        fruitsWithoutSpecialCharacters
      );

      console.log('reading the fruit', fruitsWithoutSpecialCharacters);
      setFruits(newFruitsList);
      if (
        fruitsWithoutSpecialCharacters.length === 1 &&
        uniqueNewFruits.length === 0
      ) {
        useAlert(
          `${fruitsWithoutSpecialCharacters[0].name} is already exist on the list`,
          'warning'
        );
      }
    }
  };

  const addFruit = (fruit: FruitPropsType) => {
    addFruits([fruit]);
  };

  const getAllFruitsGroup = () => {
    let allFacebookGroupsRequired: string[] = [];
    fruits.forEach((x) => {
      if (x.facebookGroupIds) {
        allFacebookGroupsRequired = [
          ...allFacebookGroupsRequired,
          ...x.facebookGroupIds,
        ];
      }
    });
    return _.uniq(allFacebookGroupsRequired);
  };
  const [requestedFacebookGroups, setRequestedFacebookGroups] = useState<
    string[]
  >([]);

  const addRequestGroupId = (groupId: string) => {
    if (!requestedFacebookGroups.find((x) => x === groupId)) {
      console.log('Add group', groupId);
      setRequestedFacebookGroups((prev) => _.uniq([...prev, groupId]));
    }
  };
  const removeRequestedGroupId = (groupId: string) => {
    if (requestedFacebookGroups.find((x) => x === groupId)) {
      console.log('removing group', groupId);
      setRequestedFacebookGroups((prev) =>
        prev.filter((gid) => gid !== groupId)
      );
    }
  };
  const availableCount = getFruitsBystatus('Available').length;

  const textedCount = getFruitsBystatus('Texted').length;

  const filteredCount = getFruitsBystatus('Filtered').length;

  const blacklistedCount = getFruitsBystatus('Blacklisted').length;

  const removedCount = getFruitsBystatus('Removed').length;

  const allCount = fruits.length;

  const reorderBasket = (startIndex: number, endIndex: number) => {
    const newFruitOrder = Array.from(fruits);
    const [removed] = newFruitOrder.splice(startIndex, 1);
    newFruitOrder.splice(endIndex, 0, removed);
    setFruits(newFruitOrder);
  };

  const resetBaskets = () => {
    localStorage.removeItem('fruits');
    setFruits([]);
  };

  const fetchInstagramPics = (
    fruits: FruitPropsType[],
    igNeedfetchFruits: FruitPropsType[]
  ) => {
    instagramPics(igNeedfetchFruits)
      .then((igFruitsWithPic) => {
        const fruitsClone = [...fruits];

        (igFruitsWithPic.data as FruitPropsType[]).forEach(
          (fruit: FruitPropsType) => {
            const idx = fruitsClone.findIndex((fc) => fc.id === fruit.id);
            fruitsClone[idx] = fruit;
          }
        );

        setFruits(fruitsClone);
      })
      .catch((err) => useErrorAlert('Can\'t fetch instagram image', err));
  };

  return (
    <HarvestingContext.Provider
      value={{
        allFruits,
        getFruitsBystatus,
        setFruitStatus,
        addFruit,
        addFruits,
        removeFruit,
        availableCount,
        textedCount,
        filteredCount,
        blacklistedCount,
        allCount,
        removedCount,
        resetBaskets,
        setFruitTexted,
        setFruitNewBlacklisted,
        reorderBasket,
        getAllFruitsGroup,
        requestedFacebookGroups,
        setFruitAsUnTexted,
        addRequestGroupId,
        setFruitNewLocked,
        removeRequestedGroupId,
        setFruitAsMember,
        setFruitAsPastor,
        removeFruitLocks,
        checkedFruit,
      }}
    >
      {children}
      {/* {allFruits.map((fruit,idx)=>(<FruitPreloader fruit={fruit} key={idx}/>))} */}
    </HarvestingContext.Provider>
  );
};
